import { Box, Divider, Typography } from '@mui/material';
import { userPages } from '../TracksPageComponents/types';
import { useContext, useEffect, useState } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import { cn } from '../../lib/cn';
import { UserDetailsContext, UserDetailsContextInterface } from '../../contexts';
import { useAuth0 } from '@auth0/auth0-react';
import { styles } from '../TracksPageComponents/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { DrawerPagesProps } from './types';
import { useAuthMode } from '../../hooks/useAuthMode';
import EnterpriseUpsellModal from '../Enterprise/EnterpriseUpsellModal';
import { EnterpriseFeature } from '../Enterprise/enterpriseUpsell';

// Pages hidden from the drawer when the caller is a guest. Direct-URL visits
// still resolve to the real route, where LockedOverlay (Billing / Team) blocks
// interaction. Hiding here cuts the discovery surface without breaking deep
// links from outside the app.
const GUEST_HIDDEN_PAGES = new Set(['Dashboard']);

export const DrawerPages: React.FC<DrawerPagesProps> = ({
  currentUser,
  currentPage,
  setCurrentPage,
  collapsed,
}) => {
  const { loginWithRedirect, user: authUser } = useAuth0();
  const { isEnterpriseAccount } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { mode } = useAuthMode();
  const isGuestMode = mode === 'guest';
  const isAdmin = authUser?.email?.endsWith('@ommuse.com') ?? false;
  const navigate = useNavigate();
  const location = useLocation();

  const [isProjectsPage, setIsProjectsPage] = useState(false);
  const [upsellFeature, setUpsellFeature] = useState<EnterpriseFeature | null>(null);
  const isEnterpriseUpsell = !!currentUser && !isEnterpriseAccount && !isGuestMode;

  useEffect(() => {
    const isProjectPage =
      window.location.pathname.includes('project') &&
      !window.location.pathname.includes('undefined');
    setIsProjectsPage(isProjectPage);

    const currentPath = window.location.pathname;
    const matchedPage = userPages.find(
      (page) =>
        currentPath === page.route || (page.route !== '/' && currentPath.startsWith(page.route))
    );

    if (matchedPage && matchedPage.name !== currentPage) {
      setCurrentPage(matchedPage.name);
    }
  }, [location.pathname, setCurrentPage, currentPage]);

  return (
    <>
      <Box sx={{ marginTop: '20px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {userPages.map((page, i) => {
            const showProjectsPage = currentUser && isEnterpriseAccount;
            const isEnterpriseOnly = page.name === 'Projects' || page.name === 'Dashboard';
            if (isEnterpriseOnly && !showProjectsPage) {
              // Non-enterprise signed-in users see Dashboard/Projects as locked
              // Enterprise teasers instead of hidden. Guests stay hidden.
              if (isEnterpriseUpsell) {
                return (
                  <Box
                    key={i}
                    sx={{
                      ...styles.pageNameContainer,
                      opacity: 0.6,
                      cursor: 'pointer',
                      ...(collapsed && { justifyContent: 'center' }),
                    }}
                    onClick={() => setUpsellFeature(page.name as EnterpriseFeature)}
                  >
                    <img
                      style={{ opacity: 0.5, width: collapsed ? '20px' : '10%' }}
                      src={page.iconPath}
                      alt="icon"
                    />
                    {!collapsed && (
                      <>
                        <Typography sx={{ fontSize: '14px', fontWeight: 400, width: '90%' }}>
                          {page.name}
                        </Typography>
                        <LockIcon sx={{ fontSize: '18px' }} />
                      </>
                    )}
                  </Box>
                );
              }
              return null;
            }
            if (isGuestMode && GUEST_HIDDEN_PAGES.has(page.name)) {
              return null;
            }

            const isCurrentRoute =
              location.pathname === page.route ||
              (page.route !== '/' && location.pathname.startsWith(page.route));

            const isHighlighted =
              (page.name === currentPage && !isProjectsPage) ||
              (page.name === 'Projects' && isProjectsPage) ||
              (page.name === 'Dashboard' && isCurrentRoute);

            return (
              <Box
                sx={{
                  ...styles.pageNameContainer,
                  display: 'flex',
                  backgroundColor: isHighlighted ? '#1F242F' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '8px',
                  ...(isHighlighted && {
                    boxShadow: 'inset 3px 0 0 0 #00e5ff, -4px 0px 12px -4px rgba(0, 229, 255, 0.4)',
                    backgroundColor: 'rgba(31, 36, 47, 0.8)', // slightly more distinct
                  }),
                  ...(collapsed && { justifyContent: 'center' }),
                }}
                key={i}
                title={collapsed ? page.name : undefined}
                onClick={() => {
                  if (
                    currentUser ||
                    page.name === 'Explore' ||
                    (isGuestMode && page.name === 'Hub')
                  ) {
                    setCurrentPage(page.name);
                    if (page.name === 'Hub') {
                      window.location.href = page.route;
                    } else if (page.name === 'Projects') {
                      navigate(page.route, {
                        state: { resetProjects: true, ts: Date.now() },
                      });
                    } else {
                      navigate(page.route);
                    }
                  } else {
                    loginWithRedirect({
                      appState: { returnTo: '/my-tracks' },
                      authorizationParams: { screen_hint: 'signup' },
                    });
                  }
                }}
              >
                <img
                  style={{
                    opacity: isHighlighted ? 1 : 0.5,
                    width: collapsed ? '20px' : '10%',
                  }}
                  src={page.iconPath}
                  alt="icon"
                />
                {!collapsed && (
                  <>
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, width: '90%' }}>
                      {page.name}
                    </Typography>
                    {!currentUser &&
                    page.name !== 'Explore' &&
                    !(isGuestMode && page.name === 'Hub') ? (
                      <LockIcon sx={{ fontSize: '18px' }} />
                    ) : null}
                  </>
                )}
              </Box>
            );
          })}
        </Box>
        {isAdmin && (
          <>
            <Divider sx={{ my: 2, borderColor: '#2A3040' }} />
            {!collapsed && (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#6B7280',
                  px: 2,
                  mb: 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Admin
              </Typography>
            )}
            <Box
              sx={{
                ...styles.pageNameContainer,
                ...(location.pathname.startsWith('/admin/support-tickets')
                  ? { backgroundColor: '#1F242F' }
                  : {}),
                ...(collapsed && { justifyContent: 'center' }),
              }}
              title={collapsed ? 'Support Tickets' : undefined}
              onClick={() => navigate('/admin/support-tickets')}
            >
              <AdminPanelSettingsIcon
                sx={{
                  width: collapsed ? '20px' : '10%',
                  fontSize: '18px',
                  opacity: location.pathname.startsWith('/admin/support-tickets') ? 1 : 0.5,
                }}
              />
              {!collapsed && (
                <Typography sx={{ fontSize: '14px', fontWeight: 400, width: '90%' }}>
                  Support Tickets
                </Typography>
              )}
            </Box>
            <div
              className={cn(
                'flex items-center gap-3 px-3 py-[10px] rounded-md cursor-pointer text-[14px] transition-colors',
                collapsed && 'justify-center',
                location.pathname.startsWith('/admin/platform-stats')
                  ? 'bg-[#1F242F] text-white'
                  : 'text-body hover:bg-[rgba(255,255,255,.03)]'
              )}
              title={collapsed ? 'Platform Stats' : undefined}
              onClick={() => navigate('/admin/platform-stats')}
            >
              <QueryStatsRoundedIcon
                className={cn(
                  'text-[18px]',
                  location.pathname.startsWith('/admin/platform-stats')
                    ? 'opacity-100'
                    : 'opacity-50'
                )}
              />
              {!collapsed && <span className="font-normal">Platform Stats</span>}
            </div>
          </>
        )}
      </Box>
      {upsellFeature && (
        <EnterpriseUpsellModal
          open={!!upsellFeature}
          feature={upsellFeature}
          userId={currentUser?.getUserId() ?? ''}
          userEmail={currentUser?.getEmail() ?? ''}
          onClose={() => setUpsellFeature(null)}
        />
      )}
    </>
  );
};
